import type { ErrorEvent, EventHint } from '@sentry/angular';

import { ErrorFrontend } from './error.utils';

/* URL absoluta: trae esquema (`https://…`) */
const URL_ABSOLUTA = /^[a-z][a-z\d+.-]*:\/\//i;

/**
 * Reduce la URL de una petición al endpoint que la identifica, para agrupar sus errores:
 * sin query ni hash, sin el host ni la ruta base de la API, sin barra final y con los
 * segmentos numéricos como `:id`. Así `https://api…/api/planillas/12/legalizar/?x=1`
 * queda `/planillas/:id/legalizar`.
 * @param {string} url - La URL de la petición (absoluta o relativa).
 * @param {string} apiUrl - La URL base de la API (`environment.apiUrl`).
 * @returns {string} - El endpoint normalizado.
 * @author Juan Corral
 */
export function normalizarEndpoint(url: string, apiUrl: string): string {
  let ruta = url.split(/[?#]/)[0];
  if (URL_ABSOLUTA.test(ruta)) {
    try {
      ruta = new URL(ruta).pathname;
    } catch {
      // Una URL malformada se queda como vino; igual se normalizan sus segmentos
    }
  }

  let base = '';
  try {
    base = new URL(apiUrl, 'http://localhost').pathname.replace(/\/+$/, '');
  } catch {
    // Sin base reconocible no hay nada que quitar
  }
  if (base && (ruta === base || ruta.startsWith(base + '/'))) ruta = ruta.slice(base.length);

  const segmentos = ruta
    .split('/')
    .filter(Boolean)
    .map((segmento) => (/^\d+$/.test(segmento) ? ':id' : segmento));
  return '/' + segmentos.join('/');
}

/**
 * Busca el `ErrorFrontend` detrás de la excepción de un evento de Sentry. Una promesa
 * rechazada sin `catch` llega envuelta por Zone (`rejection`) o por Angular
 * (`ngOriginalError`).
 * @param {unknown} excepcion - La excepción original del evento.
 * @returns {ErrorFrontend | undefined} - El error HTTP, si lo es.
 * @author Juan Corral
 */
function desenvolverErrorFrontend(excepcion: unknown): ErrorFrontend | undefined {
  if (excepcion instanceof ErrorFrontend) return excepcion;
  if (excepcion && typeof excepcion === 'object') {
    const envuelto = excepcion as { rejection?: unknown; ngOriginalError?: unknown };
    if (envuelto.rejection instanceof ErrorFrontend) return envuelto.rejection;
    if (envuelto.ngOriginalError instanceof ErrorFrontend) return envuelto.ngOriginalError;
  }
  return undefined;
}

/**
 * `beforeSend` de Sentry: separa los errores HTTP por status y endpoint.
 *
 * Por defecto Sentry agrupa un `ErrorFrontend` por la línea del servicio que lo lanza, así
 * que un 504 y un 400 de endpoints distintos acababan en el mismo issue
 * (ATLAS-FRONTEND-35). Al fingerprint por defecto se le suman el status y el endpoint, y
 * se ponen como tags para filtrar. Los errores que no son HTTP pasan intactos.
 * @param {ErrorEvent} evento - El evento a enviar.
 * @param {EventHint} pista - La pista del evento, con la excepción original.
 * @param {string} apiUrl - La URL base de la API (`environment.apiUrl`).
 * @returns {ErrorEvent} - El mismo evento, con fingerprint y tags si es un error HTTP.
 * @author Juan Corral
 */
export function agruparErrorHttp(evento: ErrorEvent, pista: EventHint, apiUrl: string): ErrorEvent {
  const error = desenvolverErrorFrontend(pista?.originalException);
  if (error?.status === undefined) return evento;

  const status = String(error.status);
  const endpoint = error.url ? normalizarEndpoint(error.url, apiUrl) : undefined;
  evento.fingerprint = endpoint ? ['{{ default }}', status, endpoint] : ['{{ default }}', status];
  evento.tags = { ...evento.tags, 'http.status': status, ...(endpoint ? { endpoint } : {}) };
  return evento;
}
